import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { CalendarX2 } from 'lucide-react'
import { ButtonLink, EmptyState } from '@/components/play'
import { loadViewerPayload, ViewerPayloadError, type ViewerPayload } from '@/lib/comrades/viewerPayload'
import { stageHandleFromPath } from '@/lib/watch/rules'
import { WatchScreenFrame, WatchScreenLoadFailed, WatchScreenMessage, WatchScreenPage, WatchScreenSkeleton, WatchShell } from './WatchScreenPage'
import '@/components/watch/watch.css'

type Loaded =
  | { shortcode: string; payload: ViewerPayload }
  | { shortcode: string; failure: 'not_found' | 'failed' }

/**
 * `/e/:shortcode`: the event's public page, the watch screen, for every event. An unknown shortcode says so. Each state
 * (loading, not found, a failed load, the screen) sits in one `WatchShell`, the site's header and footer, mounted once
 * across them. `/e/:shortcode/@handle` is that person's stage on the event: the same page (one route, so switching
 * stages keeps it and its room mounted), which reads the handle. A link by the event's id (a retired viewer's
 * `/v/<id>`, redirected here) or another spelling settles on `/e/<shortcode>`; the retired `?watch=1` is dropped.
 */
export function EventShortcodeRedirectPage() {
  const { shortcode = '', '*': rest } = useParams<{ shortcode: string; '*': string }>()
  const handle = stageHandleFromPath(rest)
  const location = useLocation()
  const normalizedShortcode = shortcode.toLowerCase()
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!normalizedShortcode) return
    let cancelled = false
    loadViewerPayload(normalizedShortcode)
      .then((payload) => {
        if (!cancelled) setLoaded({ shortcode: normalizedShortcode, payload })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const notFound = error instanceof ViewerPayloadError && error.status === 404
        setLoaded({ shortcode: normalizedShortcode, failure: notFound ? 'not_found' : 'failed' })
      })
    return () => {
      cancelled = true
    }
  }, [normalizedShortcode, attempt])

  const current = loaded?.shortcode === normalizedShortcode ? loaded : null
  const payload = current && 'payload' in current ? current.payload : null
  const canonical = payload?.event.shortcode || shortcode
  const params = new URLSearchParams(location.search)
  let settle: string | null = null
  if (payload && (canonical !== shortcode || params.has('watch'))) {
    params.delete('watch')
    const query = params.toString()
    settle = `/e/${encodeURIComponent(canonical)}${rest ? `/${rest}` : ''}${query ? `?${query}` : ''}${location.hash}`
  }

  // Settling stays in this shell, so its header and footer don't blink. Another spelling of this shortcode keeps the
  // payload, so the screen mounts once; the event's id loads it again under its shortcode, so the skeleton waits.
  let content: ReactNode
  if (!normalizedShortcode || (current && 'failure' in current && current.failure === 'not_found')) content = <EventNotFound />
  else if (payload && (!settle || canonical.toLowerCase() === normalizedShortcode)) content = <WatchScreenPage payload={payload} handle={handle} />
  else if (current && 'failure' in current) {
    content = (
      <WatchScreenFrame title={null}>
        <WatchScreenLoadFailed
          heading
          onRetry={() => {
            setLoaded(null)
            setAttempt((n) => n + 1)
          }}
        />
      </WatchScreenFrame>
    )
  } else {
    content = (
      <WatchScreenFrame fill>
        <WatchScreenSkeleton />
      </WatchScreenFrame>
    )
  }
  return (
    <WatchShell>
      {settle && <Navigate to={settle} replace />}
      {content}
    </WatchShell>
  )
}

function EventNotFound() {
  return (
    <WatchScreenFrame title={null}>
      <WatchScreenMessage heading="Event not found">
        <EmptyState
          icon={<CalendarX2 />}
          title="Event not found"
          description="Check the link you were sent."
          action={<ButtonLink to="/">Go to StreamBridge</ButtonLink>}
        />
      </WatchScreenMessage>
    </WatchScreenFrame>
  )
}
